import React, { useMemo } from 'react';
import { Box, Paper, Typography, IconButton, useTheme, CircularProgress } from '@mui/material';
import { Visibility, VisibilityOff, ContentCopy } from '@mui/icons-material';
import { IAccount } from 'common/accounts.interface';
import { useSelector } from 'react-redux';
import { IRootState } from 'stores';
import { BankingState } from 'stores/banking';
import { useSnackbar } from 'notistack';
import { cnpjFormatter } from 'utils/formatters';
import { useBankingVisibility } from '../contexts/BankingVisibilityContext';

interface AccountHeaderProps {
  account?: IAccount;
}

export const DashboardBalanceHeader: React.FC<AccountHeaderProps> = ({ account }) => {
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();
  const { showValues, toggleVisibility } = useBankingVisibility();

  const { balances } = useSelector<IRootState, BankingState>(({ banking }) => banking);

  const details = useMemo(() => {
    return {
      id: account?.id || 0,
      companyName: account?.businessName || '',
      cnpj: account?.cnpj || '',
      bankCode: account?.bankCode || '',
      agency: account?.bankBranch || '',
      accountNumber: account?.bankAccount || '',
    };
  }, [account]);

  const balance = useMemo(() => {
    return balances.find((b) => b.accountId === account?.id)?.total || 0;
  }, [balances, account]);

  const formatBalance = (amount: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(amount);
  };

  const copyText = () => {
    const text = `${details.companyName}\nCNPJ: ${cnpjFormatter(details.cnpj).maskedValue}\nBanco: ${details.bankCode} | Ag: ${
      details.agency
    } | Conta: ${details.accountNumber}`;
    navigator.clipboard.writeText(text);
    enqueueSnackbar('Dados da conta copiados para a área de transferência!', { variant: 'success' });
  };

  return (
    <Paper
      sx={{
        background: `${theme.palette.primary.dark}`,
        color: 'white',
        px: 3,
        py: 2.5,
        mb: 3,
        borderRadius: '12px',
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <Box>
          <Typography variant="h2" sx={{ fontWeight: 700, mb: 2, color: 'white' }}>
            {account?.accountType === 'PAYMENT' ? 'Conta movimento' : 'Conta recebimento'}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="h3" sx={{ fontWeight: 400, color: 'white' }}>
              {balance ? `Saldo ${showValues ? formatBalance(balance) : '••••••'}` : <CircularProgress size={20} sx={{ color: 'white' }} />}
            </Typography>
            <IconButton size="small" onClick={toggleVisibility} sx={{ color: 'white' }}>
              {showValues ? <Visibility /> : <VisibilityOff />}
            </IconButton>
          </Box>
        </Box>

        <Box sx={{ textAlign: 'right' }}>
          <Typography variant="body1" sx={{ mb: 0.5 }}>
            {details.companyName}
          </Typography>
          <Typography variant="body1" sx={{ mb: 0.5 }}>
            CNPJ: {cnpjFormatter(details.cnpj).maskedValue}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, justifyContent: 'flex-end' }}>
            <Typography variant="body1">
              Banco: {details.bankCode} | Ag: {details.agency} | Conta: {details.accountNumber}
            </Typography>
            <IconButton size="small" sx={{ color: 'white' }} onClick={copyText}>
              <ContentCopy fontSize="small" />
            </IconButton>
          </Box>
        </Box>
      </Box>
    </Paper>
  );
};
